import { LoaderFunction, LoaderFunctionArgs, redirect } from "react-router-dom";
import {
  BranchLimitsHolder,
  getDisplayedBranchLimitIds,
} from "../../BranchLimits";
import {
  AudienceHolder,
  getBranchFilters,
  isAudienceType,
  ResultsViewHolder,
} from "./ls/LSSearch";
import {
  LocationConfigurationHolder,
  SearchResultsViewType,
} from "../../LocationConfiguration";

// wrapper loader for basic, advanced, and reading level searches
export function withNormalizedSearchParams(loader?: LoaderFunction) {
  return async (args: LoaderFunctionArgs) => {
    const { request } = args;
    // branch filters
    // prefetch branch limits so they're available to the context on refresh
    console.log("prefetching branch limits");
    const { branchLimits } = await BranchLimitsHolder.fetchBranchLimits();
    const url = new URL(request.url);
    const branchFilters = getBranchFilters(url.searchParams);
    const selectedBranchIds = BranchLimitsHolder.getSelectedBranchIds();
    if (!branchFilters) {
      // if there are no branchFilters but we have selections in the context redirect with them
      if (selectedBranchIds?.length) {
        url.searchParams.set(
          "branchFilters",
          JSON.stringify(selectedBranchIds)
        );
        return redirect(url.toString());
      }
    } else {
      const displayInBranchLimitsIds = getDisplayedBranchLimitIds(branchLimits);
      const validBranchIds = branchFilters.filter((bf) =>
        displayInBranchLimitsIds.includes(bf)
      );
      // if any branchFilters are invalid, redirect without them
      if (validBranchIds.length !== branchFilters.length) {
        url.searchParams.set("branchFilters", JSON.stringify(validBranchIds));
        return redirect(url.toString());
      }
    }
    // audience
    const audienceParam = url.searchParams.get("audience");
    if (!audienceParam) {
      // if we don't have an audience param but one is in memory, redirect
      const storedAudience = AudienceHolder.getAudience();
      if (!!storedAudience) {
        url.searchParams.set("audience", storedAudience);
        return redirect(url.toString());
      }
    } else {
      // if we have an audience param but it is invalid, strip
      if (!isAudienceType(audienceParam)) {
        url.searchParams.delete("audience");
        return redirect(url.toString());
      }
    }

    // view
    const viewParam = url.searchParams.get("view");
    if (!viewParam || (viewParam !== "grid" && viewParam !== "list")) {
      const storedViewParam = ResultsViewHolder.getResultsView();
      if (!!storedViewParam) {
        url.searchParams.set(
          "view",
          storedViewParam === SearchResultsViewType.gridview ? "grid" : "list"
        );
        return redirect(url.toString());
      } else {
        const config =
          await LocationConfigurationHolder.fetchLocationConfiguration();
        const { searchResultsDesktopView, searchResultsMobileView } =
          config.pacSettings.displaySettings;
        const mediaQuery = "(min-width: 600px)"; // equivalent to SM and isMobile
        const mediaQueryList = window.matchMedia(mediaQuery);
        const resultsView = mediaQueryList.matches
          ? searchResultsDesktopView
          : searchResultsMobileView;
        ResultsViewHolder.setResultsView(resultsView);
        url.searchParams.set(
          "view",
          resultsView === SearchResultsViewType.gridview ? "grid" : "list"
        );
        return redirect(url.toString());
      }
    }

    if (loader) {
      // if there are no problems with the URL return the original loader
      return loader(args);
    } else {
      // if we don't have a loader just return null to pass through
      return null;
    }
  };
}
